/**
 * STEP 4 — Multi-Magasin RBAC frontend context.
 *
 * 2026-05-18 rectification (Carlos): `admin_magasin` keeps VENDEUR-EQUIVALENT
 * access on magasins NOT in their `assigned_magasins`. Pure read-only mode
 * is abandoned.
 *
 * Tiers exposed to the UI:
 *   adminAccess[]   — magasins where the user has full admin (stock CRUD,
 *                     prices, compta).
 *   vendeurAccess[] — magasins where the user can only act as a vendeur
 *                     (encaissement, transfert, réparation, devis).
 *   isCommercialMode(code) — true iff `code` is in vendeurAccess only.
 *
 * Falls back to safe defaults (vendeur, all magasins) when the user document
 * is incomplete.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useAuth } from './AuthContext';

const PermissionsContext = createContext(null);

const SESSION_KEY = 'pos_session_magasin_id';
const LEGACY_SESSION_KEY = 'pos_current_magasin_id'; // backward-compat read

const VALID_MAG_ROLES = ['super_admin', 'admin_magasin', 'technicien', 'vendeur'];

function readSessionMagasin() {
  try {
    const v = localStorage.getItem(SESSION_KEY);
    if (v && v.trim()) return v.trim().toUpperCase();
    const legacy = localStorage.getItem(LEGACY_SESSION_KEY);
    if (legacy && legacy.trim()) {
      const up = legacy.trim().toUpperCase();
      localStorage.setItem(SESSION_KEY, up);
      return up;
    }
  } catch (_) { /* noop */ }
  return null;
}

function writeSessionMagasin(value) {
  try {
    if (value === null || value === '' || value === 'ALL') {
      localStorage.removeItem(SESSION_KEY);
      localStorage.removeItem(LEGACY_SESSION_KEY);
    } else {
      const up = String(value).trim().toUpperCase();
      localStorage.setItem(SESSION_KEY, up);
      localStorage.setItem(LEGACY_SESSION_KEY, up);
    }
  } catch (_) { /* noop */ }
}

export const PermissionsProvider = ({ children }) => {
  const { user, api, applySessionToken } = useAuth();

  const [currentSessionMagasin, setCurrentSessionMagasinState] = useState(readSessionMagasin());
  const [allMagasins, setAllMagasins] = useState([]); // every active magasin
  const [needsMagasinPick, setNeedsMagasinPick] = useState(false);
  const [loadingMagasins, setLoadingMagasins] = useState(false);

  const magasinRole = user?.magasin_role
    || (user?.username === 'carlos' ? 'super_admin'
        : (user?.role === 'admin' || user?.role === 'super_admin') ? 'super_admin'
        : 'vendeur');
  const assignedMagasins = useMemo(
    () => (Array.isArray(user?.assigned_magasins) ? user.assigned_magasins : [])
      .map((c) => String(c).trim().toUpperCase()),
    [user]
  );
  const isSuper = magasinRole === 'super_admin';
  const isAdminMag = magasinRole === 'admin_magasin';
  const isTech = magasinRole === 'technicien';
  const isVendeur = magasinRole === 'vendeur';

  // Fetch all active magasins (backend now returns the full list for every
  // user — RBAC tiers are computed below).
  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setAllMagasins([]);
      return undefined;
    }
    setLoadingMagasins(true);
    api.get('/magasins?active_only=true')
      .then((r) => {
        if (cancelled) return;
        const items = r.data?.magasins || [];
        setAllMagasins(items);
        const codes = items.map((m) => String(m.code).trim().toUpperCase());
        const stored = readSessionMagasin();
        if (stored && codes.includes(stored)) {
          setCurrentSessionMagasinState(stored);
          setNeedsMagasinPick(false);
        } else {
          if (stored && !codes.includes(stored)) {
            writeSessionMagasin(null);
            setCurrentSessionMagasinState(null);
          }
          setNeedsMagasinPick(true);
        }
      })
      .catch(() => { /* keep silent: AuthContext owns the error UX */ })
      .finally(() => { if (!cancelled) setLoadingMagasins(false); });
    return () => { cancelled = true; };
  }, [user, api, isAdminMag, assignedMagasins]);

  // Inject X-Magasin-Session-Id on every outgoing request.
  useEffect(() => {
    if (!api) return undefined;
    const id = api.interceptors.request.use((config) => {
      try {
        const sess = readSessionMagasin();
        if (sess) {
          config.headers = config.headers || {};
          if (!config.headers['X-Magasin-Session-Id']) {
            config.headers['X-Magasin-Session-Id'] = sess;
          }
        }
      } catch (_) { /* noop */ }
      return config;
    });
    return () => api.interceptors.request.eject(id);
  }, [api, currentSessionMagasin]);

  // ISOLATION — le choix de boutique est LIÉ À LA SESSION CÔTÉ SERVEUR.
  // On appelle POST /magasins/select-active : le serveur re-signe un JWT portant
  // sid + shop_ver + active_magasin (une SEULE boutique à la fois) et PÉRIME l'ancien
  // jeton au prochain changement. Le nouveau jeton remplace effectivement l'ancien
  // (applySessionToken). L'indice localStorage/en-tête ne sert plus que d'affichage :
  // l'AUTORITÉ est le jeton signé, donc un en-tête/param forgé ne change pas le contexte.
  const setSessionMagasin = useCallback(async (code) => {
    // Vue globale / effacement (super_admin réseau) : pas de select-active, on nettoie.
    if (code === '' || code === null || String(code).toUpperCase() === 'ALL') {
      try { await api.post('/magasins/logout-session'); } catch (_) { /* best-effort */ }
      writeSessionMagasin(null);
      setCurrentSessionMagasinState(null);
      setNeedsMagasinPick(false);
      return { ok: true, global: true };
    }
    const up = String(code).trim().toUpperCase();
    try {
      const r = await api.post('/magasins/select-active', { magasin_id: up });
      const newTok = r.data?.access_token;
      if (newTok) {
        applySessionToken(newTok); // remplace EFFECTIVEMENT le JWT (autorité serveur)
      }
      writeSessionMagasin(up);
      setCurrentSessionMagasinState(up);
      setNeedsMagasinPick(false);
      return { ok: true, magasin: r.data?.active_magasin || up, sid: r.data?.sid };
    } catch (e) {
      const status = e?.response?.status;
      // Boutique hors périmètre -> refus ferme, on NE change PAS de contexte.
      if (status === 403) {
        return { ok: false, forbidden: true, error: e?.response?.data?.detail || 'Boutique hors de votre périmètre' };
      }
      // Endpoint absent (ancien backend) ou hors-ligne : repli local pour ne pas bloquer la caisse.
      if (status === 404 || !e?.response) {
        writeSessionMagasin(up);
        setCurrentSessionMagasinState(up);
        setNeedsMagasinPick(false);
        return { ok: true, magasin: up, fallback: true };
      }
      return { ok: false, error: e?.response?.data?.detail || 'Sélection de boutique impossible' };
    }
  }, [api, applySessionToken]);

  // ── RBAC tier computation ──────────────────────────────────────
  const activeCodes = useMemo(
    () => (allMagasins || []).map((m) => String(m.code).toUpperCase()),
    [allMagasins]
  );

  const adminAccess = useMemo(() => {
    if (isSuper) return activeCodes;
    if (isAdminMag) return assignedMagasins.filter((c) => activeCodes.includes(c));
    return [];
  }, [isSuper, isAdminMag, assignedMagasins, activeCodes]);

  const vendeurAccess = useMemo(() => {
    if (isSuper) return [];
    if (isAdminMag) return activeCodes.filter((c) => !assignedMagasins.includes(c));
    // vendeur / technicien → every active store
    return activeCodes;
  }, [isSuper, isAdminMag, assignedMagasins, activeCodes]);

  const canAdmin = useCallback((magasinId) => {
    if (!magasinId) return isSuper;
    return adminAccess.includes(String(magasinId).toUpperCase());
  }, [adminAccess, isSuper]);

  const canAct = useCallback((magasinId) => {
    if (!magasinId) return true;
    const up = String(magasinId).toUpperCase();
    return adminAccess.includes(up) || vendeurAccess.includes(up);
  }, [adminAccess, vendeurAccess]);

  const isCommercialMode = useCallback((magasinId) => {
    if (!magasinId) return false;
    const up = String(magasinId).toUpperCase();
    return vendeurAccess.includes(up) && !adminAccess.includes(up);
  }, [adminAccess, vendeurAccess]);

  // ── Coarse-grained capability map used by older callers ───────
  const can = useCallback((action, magasinId = null) => {
    if (action === 'admin_users' || action === 'admin_magasins'
        || action === 'admin_equipe_magasins') {
      return isSuper;
    }
    if (action === 'view_compta') {
      // Admin tier on the requested magasin (or super_admin globally).
      return magasinId ? canAdmin(magasinId) : (isSuper || isAdminMag);
    }
    if (action === 'edit_stock' || action === 'edit_prix' || action === 'archive_article') {
      return canAdmin(magasinId);
    }
    if (action === 'pos_sale' || action === 'transfer_stock'
        || action === 'create_reparation' || action === 'create_devis') {
      if (isTech && (action === 'pos_sale' || action === 'create_devis')) return false;
      return canAct(magasinId);
    }
    if (action === 'create_avoir' || action === 'use_avoir' || action === 'view_avoirs') {
      if (isTech && action === 'create_avoir') return canAdmin(magasinId);
      return canAct(magasinId);
    }
    if (action === 'cancel_avoir') {
      return magasinId ? canAdmin(magasinId) : (isSuper || isAdminMag);
    }
    return canAct(magasinId);
  }, [isSuper, isAdminMag, isTech, canAdmin, canAct]);

  // Kept for backward compat with components that still call isReadonly().
  // Now == isCommercialMode (mode commercial vendeur-equivalent).
  const isReadonly = isCommercialMode;

  const getMagasinColor = useCallback((code) => {
    if (!code) return '#6b7280';
    const m = allMagasins.find((x) => x.code === String(code).toUpperCase());
    return m?.color || '#6b7280';
  }, [allMagasins]);

  const getMagasinName = useCallback((code) => {
    if (!code) return '';
    const m = allMagasins.find((x) => x.code === String(code).toUpperCase());
    return m?.name || code;
  }, [allMagasins]);

  const value = useMemo(() => ({
    magasinRole,
    assignedMagasins,
    isSuper,
    isAdminMag,
    isTech,
    isVendeur,
    // new RBAC tier API
    adminAccess,
    vendeurAccess,
    canAdmin,
    canAct,
    isCommercialMode,
    // legacy helpers
    can,
    isReadonly,
    // session
    currentSessionMagasin,
    setSessionMagasin,
    allMagasins,
    loadingMagasins,
    needsMagasinPick,
    setNeedsMagasinPick,
    getMagasinColor,
    getMagasinName,
    VALID_MAG_ROLES,
  }), [
    magasinRole, assignedMagasins, isSuper, isAdminMag, isTech, isVendeur,
    adminAccess, vendeurAccess, canAdmin, canAct, isCommercialMode,
    can, isReadonly, currentSessionMagasin, setSessionMagasin, allMagasins,
    loadingMagasins, needsMagasinPick, getMagasinColor, getMagasinName,
  ]);

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export const usePermissions = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    return {
      magasinRole: 'vendeur',
      assignedMagasins: [],
      isSuper: false, isAdminMag: false, isTech: false, isVendeur: true,
      adminAccess: [], vendeurAccess: [],
      canAdmin: () => false, canAct: () => true,
      isCommercialMode: () => false,
      can: () => true, isReadonly: () => false,
      currentSessionMagasin: null, setSessionMagasin: () => {},
      allMagasins: [], loadingMagasins: false,
      needsMagasinPick: false, setNeedsMagasinPick: () => {},
      getMagasinColor: () => '#6b7280',
      getMagasinName: (c) => c,
      VALID_MAG_ROLES,
    };
  }
  return ctx;
};
